import styles from './InsuranceTool.module.scss';
import * as React from 'react';
import moment from 'moment-timezone';
import { ComboBox, DatePicker, PrimaryButton, Spinner, SpinnerSize, Stack, TextField, Toggle } from '@fluentui/react';
import { changeCoverage, coverage, CoverageErrorResponse, CoverageResponse } from '../../../api';
import { formatDate } from '../../../util/Date';
import { PageHeader } from '../../core/PageHeader';
import { onPageDidMount, onPageWillUnmount } from '../../../redux/core';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../../redux/types';

export const InsuranceToolTester = () => {
  const [useChangeHealthcare, setUseChangeHealthcare] = useState(false);
  const [insuranceId, setInsuranceId] = useState('47b1edfa-4ef3-410a-sdthsdt-435eh');
  const [memberId, setMemberId] = useState('0000000000');
  const [memberFirstName, setMemberFirstName] = useState('johnone');
  const [memberLastName, setMemberLastName] = useState('doeone');
  const [memberDateOfBirth, setMemberDateOfBirth] = useState<Date>(new Date('1/1/2000'));
  const [memberZipCode, setMemberZipCode] = useState('30303');
  const [checking, setChecking] = useState(false);
  const [response, setResponse] = useState<Record<string, CoverageResponse>>({});
  const [error, setError] = useState<Record<string, CoverageErrorResponse>>({});
  const [tradingPartnerServiceId, setTradingPartnerServiceId] = useState('');

  const dispatch = useDispatch();
  const payers = useSelector((state: RootState) => state.visits.visitConfig.data?.payers || []);
  const payerList = payers.map((payer) => ({ text: payer.longName, key: payer.id }));

  useEffect(() => {
    dispatch(onPageDidMount('InsuranceToolTester'));
    return () => {
      dispatch(onPageWillUnmount('InsuranceToolTester'));
    };
  }, [dispatch]);

  const canCheck = () => {
    if (tradingPartnerServiceId && !checking) {
      return true;
    }
    return false;
  };

  const mapInsuranceIdToPayerId = (insuranceId: string) => {
    const payer = payers.find((p) => p.id === insuranceId);
    return payer?.eligiblePayerCode || 'unknown';
  };

  const check = () => {
    setChecking(true);
    setError({});
    setResponse({});

    if (useChangeHealthcare) {
      changeCoverage({
        controlNumber: Math.floor(Math.random() * 1000000000)
          .toString()
          .padStart(9, '0'),
        tradingPartnerServiceId: tradingPartnerServiceId,
        provider: {
          organizationName: 'Heal Doctors',
          npi: '1285017277',
        },
        subscriber: {
          firstName: memberFirstName,
          lastName: memberLastName,
          dateOfBirth: moment(memberDateOfBirth).format('YYYYMMDD'),
          memberId: memberId,
        },
        encounter: {
          dateOfService: moment().format('YYYYMMDD'),
          serviceTypeCodes: ['30'],
        },
      })
        .then((r: any) => setResponse(r))
        .catch(setError)
        .finally(() => setChecking(false));
      return;
    }

    const promises = ['SICK_ADULT', 'TELEMED_ADULT'].map((healServiceCode) =>
      coverage({
        coverageProvider: 'changehealthcare',
        healServiceCode,
        payerId: mapInsuranceIdToPayerId(insuranceId),
        memberId: memberId,
        memberFirstName: memberFirstName || undefined,
        memberLastName: memberLastName || undefined,
        memberDOB: moment(memberDateOfBirth).format('YYYY-MM-DD'),
        placeOfServiceZipCode: memberZipCode,
        tradingPartnerServiceId: tradingPartnerServiceId,
      })
    );

    Promise.allSettled(promises)
      .then((results) => {
        const r: any = {};
        const e: any = {};
        if (results[0].status === 'fulfilled') {
          r['SICK_ADULT'] = results[0].value;
        } else {
          e['SICK_ADULT'] = results[0].reason;
        }
        if (results[1].status === 'fulfilled') {
          r['TELEMED_ADULT'] = results[1].value;
        } else {
          e['TELEMED_ADULT'] = results[1].reason;
        }
        setResponse(r);
        setError(e);
      })
      .finally(() => setChecking(false));
  };

  return (
    <div>
      <PageHeader title="Insurance Eligibility Test Scenarios" />
      <Stack horizontal tokens={{ childrenGap: 20 }}>
        <Stack disableShrink root={{ style: { minWidth: 300 } }} tokens={{ childrenGap: 8 }}>
          <Toggle
            checked={useChangeHealthcare}
            inlineLabel
            label="Use Change Healthcare Directly"
            onText="On"
            offText="Off"
            onChange={(e, checked) => setUseChangeHealthcare(checked!)}
          />
          <ComboBox
            label="Test Scenario"
            required
            selectedKey={tradingPartnerServiceId}
            autoComplete="off"
            onChange={(e, option) => option && setTradingPartnerServiceId(option.key as string)}
            options={TEST_SCENARIOS}
            disabled={checking}
          />
          <ComboBox
            label="Insurance Company"
            required
            selectedKey={insuranceId}
            allowFreeform
            autoComplete="on"
            onChange={(e, option) => option && setInsuranceId(option.key as string)}
            options={payerList}
            disabled={checking}
          />
          <TextField label="Member ID" required onChange={(e, v) => setMemberId(v!)} value={memberId} disabled={checking} />
          <TextField label="First Name" onChange={(e, v) => setMemberFirstName(v!)} value={memberFirstName} disabled={checking} />
          <TextField label="Last Name" onChange={(e, v) => setMemberLastName(v!)} value={memberLastName} disabled={checking} />
          <TextField label="Place of Service Zipcode" onChange={(e, v) => setMemberZipCode(v!)} value={memberZipCode} disabled={checking} />
          <DatePicker
            label="Date of Birth"
            isRequired
            onSelectDate={(v) => setMemberDateOfBirth(v!)}
            value={memberDateOfBirth}
            allowTextInput
            formatDate={formatDate}
            disabled={checking}
          />
          <PrimaryButton style={{ marginTop: 20, width: 180 }} text="Check" disabled={!canCheck()} onClick={() => check()} />
        </Stack>
        <Stack grow={1}>
          <div className={styles.details}>
            {checking && <Spinner size={SpinnerSize.large} />}
            {Object.keys(error).length > 0 && <pre>{JSON.stringify(error, undefined, 2)}</pre>}
            {Object.keys(response).length > 0 && <pre>{JSON.stringify(response, undefined, 2)}</pre>}
          </div>
        </Stack>
      </Stack>
    </div>
  );
};

const TEST_SCENARIOS = [
  { key: '00001', text: '00001 - This is a canned response that returns a single coverage plan. This will work for any payer.' },
  { key: '00002', text: '00002 - This is a canned response that returns a badly formatted 271. This will work for any payer.' },
  {
    key: '00003',
    text: '00003 - This is a canned response that returns a good 271 that contains maxed MSG01 field (AN..264) and EB03 repeating data element (99 repeats). This will work for any payer.',
  },
  { key: '00004', text: '00004 - This is a canned response that returns AAA Not Eligible For Inquiries. This will work for any payer.' },
  { key: '00005', text: '00005 - This is a canned response that contains non-printable characters which we need to make sure we can parse. This will work for any payer.' },
  { key: '00006', text: '00006 - A system error from the Payer.  AAA segment in the 2000A Information Source Loop with AAA01 = Y, AAA03 = 42 and AAA04 = R.' },
  { key: '00007', text: '00007 - This is a canned response that returns a single coverage plan with unused fields. This will work for any payer.' },
  {
    key: '00008',
    text: "00008 - This is a canned response that returns a 271 that contains data in deprecated fields. It is used to test our parser's handling of data in these fields. This will work for any payer.",
  },
  { key: '00009', text: '00009 - This returns a canned 271 response where the patient is a dependent.' },
  { key: '000010', text: '000010 - Hospital Inquiry: A 270 request with provider id, subscriber ID, DOB, First Name, Date of Service and service type as input parameters.' },
  {
    key: '000011',
    text: '000011 - Rehabilitation Inquiry:  A 270 request with provider id, subscriber ID, DOB, First Name, Last Name Date of Service and service type as input parameters.',
  },
  { key: '000012', text: '000012 - Medical Care Inquiry: A 270 request with provider id, subscriber ID, Date of Service and service type as input parameters.' },
  {
    key: '000013',
    text: '000013 - Rehabilitation Inquiry: A 270 request with provider id, Subscriber last name, first name, Date of Service and service type as input parameters.',
  },
  { key: '000014', text: '000014 - Vision (Optometry) Inquiry: A 270 request with provider id, subscriber ID, SSN, DOB, Date of Service and service type as input parameters.' },
  { key: '000015', text: '000015 - Home Health Care Inquiry:  A 270 request with provider id, subscriber ID, DOB, Date of Service and service type as input parameters.' },
  {
    key: '000016',
    text: '000016 - Pharmacy Inquiry: A 270 request with provider id, subscriber ID, DOB, First Name, Last Name, Date of Service and service type as input parameters.',
  },
  { key: '000017', text: '000017 - Medical Care Inquiry: A 270 request with provider id, subscriber ID, Date of Service and service type as input parameters.' },
  {
    key: '000018',
    text: '000018 - Emergency Services Inquiry: A 270 request with Information Receiver, Subscriber ID, Subscriber Last Name, Date of Service and Service Type parameters as follows is given as input.',
  },
  { key: '000019', text: '000019 - Rehabilitation Inquiry: A 270 request with provider id, subscriber SSN, DOB, Date of Service and service type as input parameters.' },
  { key: '000020', text: '000020 - Rehabilitation Inquiry: A 270 request with provider id, subscriber ID, DOB, First Name, Date of Service and service type as input parameters.' },
  { key: '000021', text: '000021 - Rehabilitation Inquiry: A 270 request with provider id, DOB, First Name, Last Name Date of Service and service type as input parameters.' },
  { key: '000022', text: '000022 - Medical Care Inquiry: A 270 request with provider id, subscriber ID, Date of Service and service type as input parameters.' },
  {
    key: '000023',
    text: '000023 - Health Benefit Plan Coverage Inquiry: A 270 request with provider id, subscriber ID, Last Name, First Name, Date of Service and service type as input parameters.',
  },
  {
    key: '000024',
    text: '000024 - Health Benefit Plan Coverage Inquiry:  A 270 request with provider id, subscriber ID, First Name, Date of Service and service type as input parameters.',
  },
  { key: '000025', text: '000025 - Rehabilitation Inquiry: A 270 request with provider id, subscriber ID, Last Name, Date of Service and service type as input parameters.' },
  { key: '000026', text: '000026 - Hospital Inquiry: A 270 request with provider id, subscriber ID, DOB, First Name, service type and diagnosis code as input parameters.' },
  { key: '000027', text: '000027 - Hospital Inquiry: A 270 request with provider id, subscriber ID, DOB, First Name, service type and provider code as input parameters.' },
  { key: '000028', text: '000028 - Hospital Inquiry: A 270 request with provider id, subscriber ID, DOB, First Name, service type and provider information as input parameters.' },
  { key: '000029', text: '000029 - Health Benefit Plan Coverage Inquiry: A 270 request with Invalid Provider ID.' },
  { key: '000030', text: '000030 - Health Benefit Plan Coverage Inquiry: A 270 request with Invalid Patient Information.' },
  { key: '000031', text: '000031 - Health Benefit Plan Coverage Inquiry: A 270 request with Inactive Coverage date as service date.' },
  { key: '000032', text: '000032 - Health Benefit Plan Coverage Inquiry: A 270 request with Duplicate. Subscriber ID.' },
  { key: '000033', text: '000033 - Health Benefit Plan Coverage Inquiry: A 270 request with Invalid Provider ID' },
  { key: '000034', text: '000034 - Rehabilitation Inquiry: A 270 request with Subscriber ID, Dependent first Name and service type code as input parameters.' },
  { key: '000035', text: '000035 - Pharmacy Inquiry: A 270 request with Subscriber ID, Dependent first Name and service type as input parameters.' },
  { key: '00036', text: '00036 - Sample response from Florida Medicaid.' },
  { key: '000036', text: '000036 - Home Health Care Inquiry: A 270 request with Subscriber ID, Dependent DOB and service type as input parameters.' },
  { key: '000037', text: '000037 - Psychiatric Inquiry: A 270 request with Subscriber ID, Dependent First Name and service type as input parameters.' },
  { key: '000038', text: '000038 - Rehabilitation Inquiry: A 270 request with Subscriber ID, Dependent DOB, Dependent Last Name and service type as input parameters.' },
  { key: '000039', text: '000039 - Different Deductible at 30 and other STC.' },
  { key: '000040', text: '000040 - EB 1 with no benefits (Active Coverage) and EB I (Non Covered).' },
  { key: '000041', text: '000041 - Test EB 6 (Inactive) and EB W (Other Source of Data).' },
  { key: '000042', text: '000042 - EB R - other additional payer.' },
  { key: '000043', text: '000043 - EB W response (Other source of Data).' },
  { key: '000044', text: '000044 - High Out of pocket remaining but no deductible in response.' },
  { key: '000045', text: '000045 - Low Deductible High Premium - many different copayments.' },
  { key: '000046', text: '000046 - Low Deductible High Premium with no coinsurance.' },
  { key: '000047', text: '000047 - Low Deductible High Premium.' },
  { key: '000048', text: '000048 - Low Deductible with no copayment.' },
  { key: '000049', text: '000049 - Multiple Services.' },
  { key: '000050', text: '000050 - No Deductible, High Out of Pocket Remaining, only Copay and coinsurance.' },
  { key: '000051', text: '000051 - No Deductible, only copay for 33,98, UC.' },
  { key: '000052', text: '000052 - No Deductible, No copayment.' },
  { key: '000053', text: '000053 - Plan with active coverage but no patient responsibility.' },
  { key: '000054', text: '000054 - Response with AD time period 25.' },
  { key: '000055', text: '000055 - Too many deductibles at service levels.' },
  { key: '000056', text: '000056 - Uniquely formatted EB segment.' },
  { key: '000067', text: '000067 - Sample use case from PAA.' },
  { key: '000068', text: '000068 - Sample use case 2 from PAA.' },
  { key: '000069', text: '000069 - Sample Use case for EB V (Cannot Process).' },
  { key: '000070', text: '000070 - Sample Use case for EB U (Contact Following Entity for Eligibility or Benefit Information).' },
  { key: '000074', text: '000074 - Sample Use case for Connecticut Medicaid.' },
  { key: '000081', text: '000081 - Sample response from WellCare.' },
  { key: '000082', text: '000082 - Sample response from Blue Cross Blue Shield Georgia.' },
  { key: '000083', text: '000083 - Sample response from Humana.' },
  { key: 'ABHFL', text: 'ABHFL - Sample use case for Aetna Better Health of Florida.' },
  { key: 'ABHKY', text: 'ABHKY - Sample use case for Aetna Better Health of Kentucky.' },
  { key: 'ABHLA', text: 'ABHLA - Sample use case for Aetna Better Health of Louisiana.' },
  { key: 'ABHMO', text: 'ABHMO - Sample use case for Aetna Better Health of Missouri.' },
  { key: 'AETNX', text: 'AETNX - Sample response from AETNA.' },
  { key: 'BCCTC', text: 'BCCTC - Sample use case for Blue Cross Blue Shield Connecticut.' },
  { key: 'BCNJC', text: 'BCNJC - Sample response from BCBS of New Jersey (Horizon).' },
  { key: 'CIGNA', text: 'CIGNA - Example of response from CIGNA for dependent.' },
  { key: 'CNTCR', text: 'CNTCR - Sample use case for Connecticare Inc.' },
  { key: 'COVON', text: 'COVON - Sample response from Coventry.' },
  { key: 'CT', text: 'CT - Sample use case for Connecticut Medicaid.' },
  { key: 'DENTAL', text: 'DENTAL - This is a canned response of benefits from Dental Payer.' },
  { key: 'HUM', text: 'HUM - Sample response from Humana.' },
  { key: 'ILMSA', text: 'ILMSA - Sample use case for Aetna Better Health of Illinois.' },
  { key: 'ISCAM', text: 'ISCAM - Sample response from Medi-CAL Portal connection.' },
  { key: 'MEDX', text: 'MEDX - Sample response from MEDEX.' },
  { key: 'MMSI', text: 'MMSI - Sample use case for Mayo.' },
  { key: 'TRICE', text: 'TRICE - Sample response from Tricare.' },
  { key: 'TX', text: 'TX - Sample response from Texas Medicaid.' },
  { key: 'UHC', text: 'UHC - Sample response from United Healthcare.' },
];
